import { useCallback, useEffect, useMemo } from "react";
import {
  Autocomplete,
  FormControl,
  type SxProps,
  TextField,
  type Theme,
} from "@mui/material";
import { TIME_WINDOW_ALL_TIME } from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.constants";
import { useQueryPipelines } from "src/queries/pipelines/pipeline.query";
import type { PipelineMinimal } from "src/queries/pipelines/pipeline.types";

type TransformedApiData = {
  app_id: number;
  app_name: string;
}[];

const transformApiData = (data: PipelineMinimal[]): TransformedApiData =>
  data
    .map((p: PipelineMinimal) => ({ app_id: p.app_id, app_name: p.app_name }))
    .sort((a, b) => a.app_name.localeCompare(b.app_name));

type ComparativeAnalysisFormPipelineProps = {
  env: string | undefined;
  pipelineName: string | undefined;
  pipelineId?: string;
  disabled?: boolean;
  onChange: (pipelineName: string) => void;
  setIsValidValue: (isValid: boolean | undefined) => void;
  isLoading?: boolean;
  formSx?: SxProps<Theme>;
};

const ComparativeAnalysisFormPipeline = ({
  env,
  pipelineName,
  disabled,
  onChange,
  setIsValidValue,
  formSx,
  isLoading,
}: ComparativeAnalysisFormPipelineProps) => {
  const {
    data: pipelines,
    isFetching,
    isFetched,
  } = useQueryPipelines<TransformedApiData>({
    params: { env_name: env, timeWindow: TIME_WINDOW_ALL_TIME, minimal: true },
    // only take what's needed for this component
    select: transformApiData,
  });

  isLoading ||= isFetching;

  const getPipelineName = useCallback(
    (name: string) => pipelines?.find((p) => p.app_name === name)?.app_name,
    [pipelines]
  );

  const onLocalChange = useCallback(
    (_e, value) => {
      onChange?.(value.app_name);
    },
    [onChange]
  );

  const value = useMemo(
    () => (isLoading || !pipelineName ? null : { app_name: pipelineName }),
    [isLoading, pipelineName]
  );

  // make sure the `pipelineName` prop (derived from URL query-param) exists in the envs list.
  // Skip validation when pipelineName is empty — an empty value is "not yet set" (undefined),
  // not "invalid" (false). Setting false for empty would race with the auto-fill effect that
  // restores pipeline2 from pipeline1, causing an infinite update loop.
  useEffect(() => {
    if (isFetched && !disabled && pipelineName) {
      const isValidPipeline = !!getPipelineName(pipelineName);
      setIsValidValue(isValidPipeline);

      if (!isValidPipeline) {
        onChange?.("");
      }
    }
  }, [setIsValidValue, getPipelineName, isFetched, disabled, pipelineName]);

  return (
    <FormControl sx={formSx} size="small">
      <Autocomplete
        loading={isLoading}
        value={value}
        autoSelect={true}
        renderInput={(params) => (
          <TextField
            {...params}
            label={isFetching ? "Fetching pipelines…" : "Pipeline"}
            // slotProps={{
            //   input: {
            //     ...params.InputProps,
            //     className: "text-xs! pr-6!",
            //   },
            // }}
          />
        )}
        onChange={onLocalChange}
        disabled={isFetching || disabled}
        disableClearable
        size="small"
        options={pipelines || []}
        includeInputInList={true}
        isOptionEqualToValue={(option, value) => option.app_name === value.app_name}
        getOptionKey={(option) => option.app_id}
        getOptionLabel={(option) => option.app_name ?? ""}
        slotProps={{
          listbox: { sx: { maxHeight: 500 } },
          paper: {
            sx: {
              width: "max-content",
              minWidth: "100%",
            },
          },
        }}
        data-test-id="compare-runs--pipeline-select"
      />
    </FormControl>
  );
};

export default ComparativeAnalysisFormPipeline;
